import { Platform } from 'react-native';

import { LIST_BUCKET } from '@todo/core/list-queries';
import { getPrefetched, prefetchElementId, serializePrefetch } from '@todo/core/static-prefetch';

/**
 * Bakes an index page's build-time row list into its static HTML as JSON.
 *
 * Without this the page would render its cards during the export (from the
 * in-process seed) but the browser's first render would find an empty list and
 * React would throw a hydration mismatch (#418). useEvents/useTours read this
 * <script> back via getPrefetched so both renders agree; the live fetch then
 * replaces it. Returns null when there's no seed — a failed prefetch, or
 * native, where none of this runs.
 *
 * Deliberately a plain function rather than a component, because the result is
 * placed inside <Head>: expo-router's vendored react-helmet-async asserts that
 * every child of <Helmet> is a literal head element and throws
 * ("You may be attempting to nest <Helmet> components within each other") the
 * moment a child's `type` is a function. Calling this inline —
 * `{listSeedScript(KEY)}` — hands Helmet a <script> element, which it accepts.
 * A component here silently fails the whole page's static render.
 */
export function listSeedScript(seedKey: string) {
  const rows = getPrefetched<unknown[]>(LIST_BUCKET, seedKey);
  if (rows === undefined) return null;

  return (
    <script id={prefetchElementId(LIST_BUCKET, seedKey)} type="application/json">
      {serializePrefetch(rows)}
    </script>
  );
}

/**
 * listSeedScript for a self-contained block that bakes its own seed in the
 * body rather than relying on the page to put it in <Head> (CityCrossSell) —
 * the same mechanism the site footer uses for its page lists. Web only: there's
 * no <script> on native, where nothing was exported and the list hooks fetch.
 */
export function BodyListSeed({ seedKey }: { seedKey: string }) {
  if (Platform.OS !== 'web') return null;
  const rows = getPrefetched<unknown[]>(LIST_BUCKET, seedKey);
  if (rows === undefined) return null;

  return (
    <script
      id={prefetchElementId(LIST_BUCKET, seedKey)}
      type="application/json"
      // Raw, not a text child: the JSON must reach the DOM unescaped for
      // readBakedState's JSON.parse. serializePrefetch escapes `<`.
      dangerouslySetInnerHTML={{ __html: serializePrefetch(rows) }}
    />
  );
}
